import { CheckCircle, Error as ErrorIcon, Warning } from "@mui/icons-material";
import { DocumentLoadStatus, TargetSchema, LoadOperation, ResourceName } from "@/types";
import { CLAIMANT_RELATIONSHIP_CHOICES } from "@/types/claimant";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDate } from "@/utils/formatters/dateFormatter";

// =============================================================================
// TYPES
// =============================================================================

export type FieldFormat = "text" | "date" | "currency" | "id" | "claimant_id_ref" | "relationship";

export interface FieldConfig {
  field: string;
  label: string;
  format?: FieldFormat;
}

export interface EntityTypeConfig {
  label: string;
  labelPlural: string;
  resource: ResourceName;
  fields: FieldConfig[];
  defaultPerPage?: number;
}

export interface GroupedEntityIds {
  targetSchema: TargetSchema;
  entityIds: string[];
  operations: Record<string, LoadOperation>;
}

export interface OperationCounts {
  created: number;
  updated: number;
  unchanged: number;
}

// =============================================================================
// CONFIGURATION
// =============================================================================

export const ENTITY_CONFIG: Record<TargetSchema, EntityTypeConfig> = {
  [TargetSchema.CLAIMANT]: {
    label: "Claimant",
    labelPlural: "Claimants",
    resource: ResourceName.CLAIMANTS,
    fields: [
      { field: "claimant_name", label: "Name" },
      { field: "member_id", label: "Member ID", format: "id" },
      { field: "claimant_gender", label: "Gender" },
      { field: "claimant_relationship", label: "Relationship", format: "relationship" },
    ],
  },
  [TargetSchema.CLAIM_ITEM]: {
    label: "Claim",
    labelPlural: "Claims",
    resource: ResourceName.CLAIM_ITEMS,
    defaultPerPage: 25,
    fields: [
      { field: "claimant_id_ref", label: "Claimant", format: "claimant_id_ref" },
      { field: "date_of_service_start", label: "Date of Service", format: "date" },
      { field: "procedure_description", label: "Procedure" },
      { field: "primary_diagnosis", label: "Diagnosis", format: "id" },
      { field: "billed_amount", label: "Billed", format: "currency" },
      { field: "paid_amount", label: "Paid", format: "currency" },
    ],
  },
  [TargetSchema.STOPLOSS_CLAIM]: {
    label: "Stop-Loss Claim",
    labelPlural: "Stop-Loss Claims",
    resource: ResourceName.STOPLOSS_CLAIMS,
    fields: [
      { field: "aegle_id", label: "Aegle ID", format: "id" },
      { field: "claimant_id_ref", label: "Claimant", format: "claimant_id_ref" },
      { field: "claim_status", label: "Status" },
      { field: "total_paid_amount", label: "Total Paid", format: "currency" },
    ],
  },
  [TargetSchema.REIMBURSEMENT]: {
    label: "Reimbursement",
    labelPlural: "Reimbursements",
    resource: ResourceName.REIMBURSEMENTS,
    fields: [
      { field: "claimant_id_ref", label: "Claimant", format: "claimant_id_ref" },
      { field: "reimbursement_amount", label: "Amount", format: "currency" },
      { field: "requested_amount", label: "Requested", format: "currency" },
      { field: "received_date", label: "Received", format: "date" },
    ],
  },
  [TargetSchema.CLIENT]: {
    label: "Client",
    labelPlural: "Clients",
    resource: ResourceName.CLIENTS,
    fields: [{ field: "client_name", label: "Name" }],
  },
  [TargetSchema.POLICY]: {
    label: "Policy",
    labelPlural: "Policies",
    resource: ResourceName.POLICIES,
    fields: [{ field: "policy_number", label: "Policy Number" }],
  },
};

export const OPERATION_COLORS: Record<LoadOperation, "success" | "primary" | "default"> = {
  [LoadOperation.CREATED]: "success",
  [LoadOperation.UPDATED]: "primary",
  [LoadOperation.UNCHANGED]: "default",
};

export const STATUS_CONFIG: Record<
  DocumentLoadStatus,
  { icon: typeof CheckCircle; color: "success" | "warning" | "error"; label: string }
> = {
  [DocumentLoadStatus.SUCCESS]: { icon: CheckCircle, color: "success", label: "Load Successful" },
  [DocumentLoadStatus.PARTIAL_SUCCESS]: { icon: Warning, color: "warning", label: "Partial Success" },
  [DocumentLoadStatus.FAILED]: { icon: ErrorIcon, color: "error", label: "Load Failed" },
};

// =============================================================================
// UTILITIES
// =============================================================================

export const formatFieldValue = (
  value: unknown,
  format: FieldFormat = "text",
  claimantLookup?: Record<string, string>
): string => {
  if (value === null || value === undefined || value === "") return "—";

  switch (format) {
    case "currency":
      return formatUSD(Number(value) || 0);
    case "date":
      return formatDate(String(value));
    case "id":
      return String(value);
    case "claimant_id_ref":
      return claimantLookup?.[String(value)] || "—";
    case "relationship":
      // Map relationship enum to its display name — single source of truth in
      // CLAIMANT_RELATIONSHIP_CHOICES. Notably `"self"` displays as
      // `"Subscriber"`, matching the rest of the app's claimant surfaces.
      return CLAIMANT_RELATIONSHIP_CHOICES.find((c) => c.id === String(value))?.name ?? String(value);
    default:
      return String(value);
  }
};

export const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  return path.split(".").reduce<unknown>((acc, part) => {
    if (acc && typeof acc === "object") return (acc as Record<string, unknown>)[part];
    return undefined;
  }, obj);
};

export const countOperations = (operations: Record<string, LoadOperation>): OperationCounts => {
  const counts: OperationCounts = { created: 0, updated: 0, unchanged: 0 };
  Object.values(operations).forEach((op) => counts[op]++);
  return counts;
};

export const formatOperationSummary = (counts: OperationCounts): string => {
  const parts: string[] = [];
  if (counts.created > 0) parts.push(`${counts.created} created`);
  if (counts.updated > 0) parts.push(`${counts.updated} updated`);
  if (counts.unchanged > 0) parts.push(`${counts.unchanged} unchanged`);
  return parts.join(", ");
};
